/** @jsx jsx */
import { css, jsx } from "@emotion/core"
import React from "react"
import { P } from "@cd2/text"
import { IRecord } from "../../types/schema"
import { KeyValueList } from "../KeyValueList"
import { titleize } from "@cd2/tools/build/es6"
import { format } from "date-fns"
import { createHoverLoader } from "./createHoverLoader"
import { buildRoute, eRoutes } from "../../routes"
import { useGlobalState } from "../../utils/globalState"

type IFlowResult = IRecord<
  "flowResults",
  | "name"
  | "identifier"
  | "notes"
  | "flowName"
  | "status"
  | "completedDate"
  | "createdAt"
  | "subject"
> & {
  employees: IRecord<"employees", "firstName" | "lastName">[]
  childFlowResult?: IRecord<"flowResults", "name" | "flowName" | "identifier">
  parentFlowResults: IRecord<"flowResults", "name" | "flowName" | "identifier">[]
  location?: IRecord<"locations", "name">
  user: IRecord<"skeem_users", "name">
  invitedUsers: IRecord<"skeem_users", "name">[]
  flow?: IRecord<"flows"> & {
    groups: IRecord<"flowGroups", "name">[]
    flowLinks: (IRecord<"flowLinks"> & { group: IRecord<"flowGroups", "name"> })[]
  }
}

export const HoverFlowResultsLoader = createHoverLoader<IFlowResult>({
  model: "flowResults",
  attributes: [
    "name",
    "identifier",
    "notes",
    "flowName",
    "status",
    "completedDate",
    "createdAt",
    "subject",
    {
      name: "flow",
      attributes: [
        { name: "groups", attributes: ["name"] },
        { name: "flowLinks", attributes: [{ name: "group", attributes: ["name"] }] },
      ],
    },
    { name: "user", attributes: ["name"] },
    { name: "invitedUsers", attributes: ["name"] },
    { name: "employees", attributes: ["firstName", "lastName"] },
    { name: "location", attributes: ["name"] },
    { name: "childFlowResult", attributes: ["name", "identifier"] },
    { name: "parentFlowResults", attributes: ["name", "identifier"] },
  ],
  viewLink: record => buildRoute(eRoutes["/case-coaching/:id/results"], record),
  Target({ record }) {
    return record?.name || record?.identifier
  },
  HoverContent({ record }) {
    const globalState = useGlobalState()

    return (
      <KeyValueList
        data={[
          { label: "Case coaching type", value: record?.flowName },
          { label: "Identifier", value: record?.identifier },
          { label: "Subject", value: record?.subject },
          {
            label: "Status",
            value: record?.completedDate
              ? "Closed"
              : record?.status
              ? titleize(
                  ["pending", "inProgress"].includes(record.status) ? "in progress" : record.status,
                )
              : "",
          },
          { label: "Taken by", value: record?.user?.name },
          {
            label: "Date Started",
            value: record?.createdAt ? format(new Date(record?.createdAt), "dd/MM/yy") : null,
          },
          {
            label: "Additional assessors",
            value: (
              <React.Fragment>
                {record?.invitedUsers.map(e => (
                  <P key={e.id} blockSpacing={0}>
                    {e.name}
                  </P>
                ))}
                {(!record?.invitedUsers || record.invitedUsers.length === 0) && (
                  <P>No additional assessors</P>
                )}
              </React.Fragment>
            ),
          },
          {
            label: "Employees",
            value: (
              <React.Fragment>
                {record?.employees.map(e => (
                  <P key={e.id} blockSpacing={0}>
                    {[e.firstName, e.lastName].join(" ")}
                  </P>
                ))}
                {(!record?.employees || record.employees.length === 0) && (
                  <P>No employees were added</P>
                )}
              </React.Fragment>
            ),
          },
          {
            label: "Parent Cases",
            value: record?.parentFlowResults && record.parentFlowResults.length > 0 && (
              <React.Fragment>
                {record?.parentFlowResults.map(e => (
                  <P key={e.id} blockSpacing={0}>
                    {e.name || e.identifier}
                  </P>
                ))}
              </React.Fragment>
            ),
            returnNullOnEmpty: true,
          },
          {
            label: "Location",
            value: globalState.settings.useLocations ? record?.location?.name : null,
            returnNullOnEmpty: true,
          },
          {
            label: "Case Notes",
            value: (
              <P
                css={css`
                  white-space: pre-wrap;
                `}
              >
                {record?.notes || "No notes were added."}
              </P>
            ),
          },
        ]}
      />
    )
  },
})
